import { useEffect, useState } from 'react';

import cn from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import DatePickerComponent from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import styles from './DatePicker.module.scss';

export const DatePicker = ({
  value,
  useDefaultToday = true,
  minDate,
  maxDate,
  format = 'dd-MM-yyyy',
  outputFormat = 'DD/MM/YYYY',
  className,
  onChange = () => {},
  error,
  noErrorMessage = false,
}) => {
  const isEmpty = v => v == null || v === '';

  const [date, setDate] = useState(() => {
    if (!isEmpty(value)) {
      const dateValue = moment(value, outputFormat);
      if (dateValue.isValid()) return dateValue.toDate();
    }
    return useDefaultToday ? new Date() : null;
  });

  // Sync from controlled value; when value is empty or unparseable, useDefaultToday decides fallback
  useEffect(() => {
    if (value === undefined) return; // Skip if uncontrolled

    if (isEmpty(value)) {
      setDate(useDefaultToday ? new Date() : null);
      return;
    }
    const dateValue = moment(value, outputFormat);
    setDate(dateValue.isValid() ? dateValue.toDate() : useDefaultToday ? new Date() : null);
  }, [value, useDefaultToday, outputFormat]);

  // Send date value in output format
  useEffect(() => {
    if (!date) return;

    const newValue = moment(date).format(outputFormat);
    onChange(newValue);
  }, [date]);

  return (
    <div className={cn(styles.root, { [styles.rootInvalid]: !!error, [className]: className })}>
      <DatePickerComponent
        selected={date}
        minDate={minDate}
        maxDate={maxDate}
        dateFormat={format}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        onChange={setDate}
      />
      {error && error?.message && !noErrorMessage && (
        <span className={styles.error}>{error.message}</span>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  value: PropTypes.string,
  useDefaultToday: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  format: PropTypes.string,
  outputFormat: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  noErrorMessage: PropTypes.bool,
};
